import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  FaLinkedin,
  FaGithub,
  FaEnvelope,
  FaArrowDown,
  FaBars,
  FaTimes,
} from 'react-icons/fa';
import LanguageSwitcher from './LanguageSwitcher';
import profile from '../data/profile';

const iconFor = (icon) => {
  switch (icon) {
    case 'linkedin':
      return <FaLinkedin />;
    case 'github':
      return <FaGithub />;
    case 'mail':
      return <FaEnvelope />;
    default:
      return null;
  }
};

const Header = () => {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navItems = [
    { key: 'home', href: '#home' },
    { key: 'about', href: '#about' },
    { key: 'resume', href: '#resume' },
    { key: 'principle', href: '#testimonials' },
    { key: 'contact', href: '#contact' },
  ];

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (!el) return;
    const sectionTop = el.getBoundingClientRect().top + window.scrollY;
    const paddingTop = parseFloat(getComputedStyle(el).paddingTop) || 0;
    const navH = document.querySelector('.navbar')?.offsetHeight ?? 72;
    const top = sectionTop + paddingTop - navH - 16;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  };

  const country = profile.country[i18n.language] || profile.country.en;
  const headline = t('hero.greeting');
  const occupation = t('hero.occupation');

  return (
    <>
      <nav className={`navbar ${scrolled ? 'is-scrolled' : ''}`}>
        <div className="nav-inner">
          <motion.a
            href="#home"
            onClick={(e) => handleNavClick(e, '#home')}
            className="brand clickable"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            aria-label="Home"
          >
            <img src="images/logo.png" alt="Logo" className="brand-logo" />
          </motion.a>

          <ul className={`nav-links ${menuOpen ? 'is-open' : ''}`}>
            {navItems.map((item, i) => (
              <motion.li
                key={item.key}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.06 }}
              >
                <a
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className="clickable"
                >
                  {t(`nav.${item.key}`)}
                </a>
              </motion.li>
            ))}
            <li className="nav-lang-mobile">
              <LanguageSwitcher />
            </li>
          </ul>

          <div className="nav-right">
            <div className="nav-lang-desktop">
              <LanguageSwitcher />
            </div>
            <button
              type="button"
              className="nav-burger clickable"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>
      </nav>

    <header id="home" className="hero">
      <div className="hero-content">
        <motion.span
          className="hero-greeting"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          {headline}
        </motion.span>

        <motion.h1
          className="hero-name"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          <span className="hero-name-text">{t('hero.firstName')} </span>
          <span className="hero-name-grad">{t('hero.lastName')}</span>
          <span className="hero-name-dot">.</span>
        </motion.h1>

        <motion.h2
          className="hero-occupation"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35 }}
        >
          {occupation} · {country}
        </motion.h2>

        <motion.p
          className="hero-desc"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          {t('hero.description')}
        </motion.p>

        <motion.div
          className="hero-actions"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.65 }}
        >
          <a
            href="#about"
            onClick={(e) => handleNavClick(e, '#about')}
            className="btn btn-primary clickable"
          >
            {t('hero.cta')}
          </a>
          {/* <a
            href={profile.resumeDownload}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-ghost clickable"
          >
            {t('hero.download')}
          </a> */}
        </motion.div>

        <motion.ul
          className="hero-social"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          {profile.social.map((s) => (
            <li key={s.name}>
              <a
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.name}
                className="clickable"
              >
                {iconFor(s.icon)}
              </a>
            </li>
          ))}
        </motion.ul>
      </div>

      <motion.a
        href="#about"
        onClick={(e) => handleNavClick(e, '#about')}
        className="hero-scroll clickable"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1 }}
        aria-label={t('hero.scroll')}
      >
        <motion.span
          className="hero-scroll-icon"
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.6 }}
        >
          <FaArrowDown />
        </motion.span>
        <span className="hero-scroll-text">{t('hero.scroll')}</span>
      </motion.a>
    </header>
    </>
  );
};

export default Header;
